import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import SunEditor from 'suneditor-react';
import 'suneditor/dist/css/suneditor.min.css';
import { EditorShell } from './RichTextEditor.styles';

/**
 * Toolbar grouped like TinyMCE (Edit | Format | Insert | View).
 * SunEditor separates groups with adjacent arrays (not '|'); '/' is a line break.
 * Video insert is intentionally omitted.
 */
export const DEFAULT_BUTTON_LIST = [
  ['undo', 'redo'],
  ['font', 'fontSize', 'formatBlock'],
  ['bold', 'underline', 'italic', 'strike', 'subscript', 'superscript'],
  ['fontColor', 'hiliteColor', 'removeFormat'],
  ['outdent', 'indent', 'align', 'list', 'lineHeight', 'horizontalRule'],
  ['table', 'link', 'image'],
  ['fullScreen', 'showBlocks', 'codeView', 'preview'],
];

/** Drop invalid TinyMCE-style '|' separators; SunEditor uses array groups instead. */
const sanitizeButtonList = (list) => {
  if (!Array.isArray(list)) return DEFAULT_BUTTON_LIST;
  const cleaned = list
    .map((group) => {
      if (group === '/') return '/';
      if (!Array.isArray(group)) return null;
      const buttons = group.filter((btn) => btn && btn !== '|');
      return buttons.length ? buttons : null;
    })
    .filter(Boolean);
  return cleaned.length ? cleaned : DEFAULT_BUTTON_LIST;
};

const DEFAULT_FONTS = [
  'Arial',
  'Comic Sans MS',
  'Courier New',
  'Georgia',
  'Helvetica',
  'Impact',
  'Tahoma',
  'Times New Roman',
  'Trebuchet MS',
  'Verdana',
];

/**
 * Reusable rich-text editor (SunEditor) replacing TinyMCE.
 * Keeps the previous API: value, handleChange, height.
 */
const RichTextEditor = ({
  value,
  handleChange,
  height,
  placeholder,
  disabled,
  buttonList,
}) => {
  const editorRef = useRef(null);
  const lastExternalValue = useRef(value || '');

  const getSunEditorInstance = (sunEditor) => {
    editorRef.current = sunEditor;
  };

  // Sync when parent changes value from outside (e.g. load / reset), without fighting typing.
  useEffect(() => {
    const next = value || '';
    if (next === lastExternalValue.current) return;
    lastExternalValue.current = next;
    if (editorRef.current) {
      editorRef.current.setContents(next);
    }
  }, [value]);

  // Insert selected image files as data URLs (parity with TinyMCE file_picker_callback).
  const handleImageUploadBefore = (files, _info, uploadHandler) => {
    const file = files && files[0];
    if (!file) {
      uploadHandler();
      return undefined;
    }
    const reader = new FileReader();
    reader.onload = () => {
      uploadHandler({
        result: [
          {
            url: reader.result,
            name: file.name,
            size: file.size,
          },
        ],
      });
    };
    reader.onerror = () => uploadHandler();
    reader.readAsDataURL(file);
    return undefined;
  };

  return (
    <EditorShell>
      <SunEditor
        getSunEditorInstance={getSunEditorInstance}
        defaultValue={value || ''}
        onChange={(content) => {
          lastExternalValue.current = content;
          if (handleChange) handleChange(content);
        }}
        disable={!!disabled}
        setOptions={{
          height: String(height || 500),
          minHeight: '160px',
          width: '100%',
          resizingBar: true,
          placeholder: placeholder || 'Start typing...',
          defaultTag: 'p',
          font: DEFAULT_FONTS,
          buttonList: sanitizeButtonList(buttonList || DEFAULT_BUTTON_LIST),
          formats: [
            'p',
            'div',
            'h1',
            'h2',
            'h3',
            'h4',
            'h5',
            'h6',
            'blockquote',
            'pre',
          ],
          imageFileInput: true,
          imageUrlInput: true,
          videoFileInput: false,
          attributesWhitelist: {
            all: 'style|data-.+',
          },
        }}
        onImageUploadBefore={handleImageUploadBefore}
      />
    </EditorShell>
  );
};

RichTextEditor.defaultProps = {
  value: '',
  height: '500',
  placeholder: '',
  disabled: false,
  buttonList: null,
  handleChange: () => {},
};

RichTextEditor.propTypes = {
  value: PropTypes.string,
  handleChange: PropTypes.func,
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  buttonList: PropTypes.array,
};

export default RichTextEditor;
